import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  useTheme,
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  // AccountTree as FlowsIcon,
  Send as CampaignIcon,
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  Logout as LogoutIcon,
  // Settings as SettingsIcon,
  AutoFixHigh as AutomationsIcon,
  Event as BookingsIcon,
  LibraryBooks as CatalogIcon,
  //AdminPanelSettings as AdminIcon,
  WhatsApp as WhatsAppIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material'

import { useAuth } from '../contexts/AuthContext'
import { useSMBContext } from '../contexts/SMBContext'
import { useNotification } from '../contexts/NotificationContext'
import { usePushNotifications } from '../hooks/usePushNotifications'
import { useSMBNavState } from '../hooks/useSMBNavState'
import { WHATSAPP_BUTTON_BG_COLOR, WHATSAPP_BUTTON_HOVER_BG_COLOR } from '../constants/whatsapp'

const drawerWidth = 260

const Layout: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isLoading } = useAuth()
  const { showNotification } = useNotification()
  const { subscribeToNotifications, unsubscribeFromNotifications } = usePushNotifications()
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>(
    typeof window !== 'undefined' ? (window.Notification?.permission || 'default') : 'default'
  )

  const { smbs, loading: smbsLoading } = useSMBContext()
  const isDemo = user?.email === 'demo@lyftr.ai'
  const noSmbsOnboarded = !smbsLoading && !smbs.some((smb) => Boolean((smb as any).onboarding_completed))
  const showContactUsBtn = isDemo || noSmbsOnboarded
  const shopifyShopDomain = localStorage.getItem('shopify_shop_domain')
  const contactUsText = shopifyShopDomain 
    ? `Hi Lyftr AI, I'd like to book an appointment for my Shopify store, ${shopifyShopDomain}. We receive around 15 inquiries daily and are looking for help automating replies.`
    : "Hi Lyftr AI, I'd like to book an appointment."
  const contactUsWhatsAppURL = `https://wa.me/919315689780?text=${encodeURIComponent(contactUsText)}`

  // Redirect to login if not authenticated (but wait for auth check to complete)
  // Preserve query params (e.g. ?status=public_install_success) so Login can act on them
  React.useEffect(() => {
    if (!isLoading && !user) {
      navigate({ pathname: '/login', search: location.search })
    }
  }, [user, isLoading, navigate, location.search])

   // Soft-prompt for notification permission on Dashboard
  React.useEffect(() => {
    if (user && !isLoading) {
      const sessionKey = 'show_notification_prompt';
      const promptShown = sessionStorage.getItem(sessionKey);

      if (notifPermission === 'default' && location.pathname === '/dashboard' && !promptShown) {
        const timer = setTimeout(() => {
          showNotification(
            'Get real-time alerts for new messages!', 
            'info', 
            15000, 
            { 
              label: 'Enable', 
              onClick: () => subscribeToNotifications().then(() => setNotifPermission(Notification.permission)) 
            }
          );
          sessionStorage.setItem(sessionKey, 'true');
        }, 1500);
        return () => clearTimeout(timer);
      } else if (notifPermission === 'denied' && !promptShown) {
        // Only show blocked warning once per session or on load
        showNotification('Push notifications are currently disabled. You can enable them anytime in your browser settings', 'warning', 8000);
        sessionStorage.setItem(sessionKey, 'true');
      }
    }
  }, [user, isLoading, location.pathname, notifPermission, showNotification, subscribeToNotifications]);

  // Listen for navigation messages from Service Worker (Background notification clicks)
  React.useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'NAVIGATE') {
          const url = event.data.url;
          try {
            const urlObj = new URL(url, window.location.origin);
            if (urlObj.origin === window.location.origin) {
              const relativePath = urlObj.pathname + urlObj.search + urlObj.hash;
              navigate(relativePath);
            }
          } catch (e) {
            console.error('SW Navigation Error:', e);
          }
        }
      };
      
      navigator.serviceWorker.addEventListener('message', handleMessage);
      return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
    }
  }, [navigate]);

  // Feature Lock Guard state encapsulated in custom hook
  const { smbNavState } = useSMBNavState(user, location.pathname)

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null)
  }

  const handleLogout = async () => {
    try {
      await unsubscribeFromNotifications()
    } catch (e) {
      console.warn('FCM unsubscription failed during logout', e)
    }
    logout()
    handleUserMenuClose()
    navigate('/login')
  }

  /*const handleAdminAccess = () => {
    navigate('/admin')
    handleUserMenuClose()
  }*/

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Catalog', icon: <CatalogIcon />, path: '/catalog' },
    { text: 'Conversations', icon: <ChatIcon />, path: '/conversations' },
    { text: 'Leads', icon: <PeopleIcon />, path: '/leads' },
    { text: 'Campaigns', icon: <CampaignIcon />, path: '/campaigns' },
    // { text: 'Flows', icon: <FlowsIcon />, path: '/flows' },
    { text: 'Automations', icon: <AutomationsIcon />, path: '/automations' },
    { text: 'Bookings', icon: <BookingsIcon />, path: '/bookings' },
    /*{ text: 'Admin (Dev)', icon: <AdminIcon />, path: '/admin' },*/
  ]

  const mobileBottomItems = [
    { text: 'Home', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Chats', icon: <ChatIcon />, path: '/conversations' },
    { text: 'Automations', icon: <AutomationsIcon />, path: '/automations' },
    { text: 'Bookings', icon: <BookingsIcon />, path: '/bookings' },
    { text: 'Campaigns', icon: <CampaignIcon />, path: '/campaigns' },
    { text: 'Catalog', icon: <CatalogIcon />, path: '/catalog' },
    // { text: 'Flows', icon: <FlowsIcon />, path: '/flows' },
    { text: 'Leads', icon: <PeopleIcon />, path: '/leads' },
  ]

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flexGrow: 1 }}>
        <Toolbar>
          <Box 
            onClick={() => navigate('/dashboard')} 
            sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
          >
            <img src="/lyftr_ai_circle_logo.png" alt="Lyftr AI Logo" style={{ width: 42, height: 42, objectFit: 'contain' }} />
            <Typography variant="h6" fontWeight="bold">
              Lyftr AI
            </Typography>
          </Box>
        </Toolbar>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path)
                }}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      {showContactUsBtn && (
        <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button
            component="a"
            href={contactUsWhatsAppURL}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            fullWidth
            startIcon={<WhatsAppIcon />}
            sx={{
              borderRadius: 2,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              bgcolor: WHATSAPP_BUTTON_BG_COLOR,
              color: '#fff',
              transition: 'all 0.2s ease-in-out',
              boxShadow: '0 2px 4px rgba(37, 211, 102, 0.2)',
              '&:hover': {
                bgcolor: WHATSAPP_BUTTON_HOVER_BG_COLOR,
                boxShadow: '0 4px 8px rgba(37, 211, 102, 0.4)',
                transform: 'translateY(-1px)',
              }
            }}
          >
            Talk to an advisor
          </Button>
        </Box>
      )}
    </Box>
  )

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', width: '100%' }}>
        <Typography><CircularProgress/></Typography>
      </Box>
    )
  }

  if (!user) {
    return null // Redirecting to login
  }

  // Global style to prevent scrollbar-induced layout shifts
  const globalStyles = (
    <style>{`
      html {
        overflow-y: scroll;
        scrollbar-gutter: stable;
      }
    `}</style>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {globalStyles}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          right: 0,
          scrollbarGutter: 'stable',
          borderRadius: 0,
          boxShadow: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>

          <Box 
            onClick={() => navigate('/dashboard')} 
            sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
          >
            <img src="/lyftr_ai_circle_logo.png" alt="Lyftr AI Logo" style={{ width: 42, height: 42, objectFit: 'contain' }} />
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          
           {/* Notifications Bell with Status */}
          <IconButton
            title={notifPermission === 'default' ? "Enable Notifications" : notifPermission === 'denied' ? "Notifications are Blocked" : "Notifications are Enabled"} 
            color="inherit" 
            sx={{ mr: 1 }} 
            onClick={() => {
              if (notifPermission === 'denied') {
                showNotification('Push notifications are currently disabled. You can enable them anytime in your browser settings', 'warning');
              } else if(notifPermission === 'default') {
                subscribeToNotifications().then(() => setNotifPermission(Notification.permission));
              }
              else{
                showNotification('Notifications are already enabled', 'success')
              }
            }}
          >
            <Badge 
              variant={notifPermission === 'default' ? 'dot' : 'standard'} 
              color={notifPermission === 'default' ? 'warning' : 'error'}
              overlap="circular"
            >
              {notifPermission === 'denied' ? (
                <NotificationsOffIcon sx={{ color: 'error.light' }} />
              ) : (
                <NotificationsIcon />
              )}
            </Badge>
          </IconButton>
          
          {/* User Menu */}
          <IconButton
            onClick={handleUserMenuOpen}
            sx={{ p: 0 }}
          >
            <Avatar
              alt={user.name ?? user.email ?? ''}
              src={(user as any)?.profile_picture_url ?? (user as any)?.avatar_url ?? ''}
              sx={{ width: 40, height: 40 }}
            >
              {(user.name || user.email || 'U').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            onClick={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => { handleUserMenuClose(); navigate('/billing'); }}>
              <ListItemIcon>
                <CreditCardIcon fontSize="small" />
              </ListItemIcon>
              Billing & Plans
            </MenuItem>
            {/* <MenuItem>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem> */}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRadius: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          pb: { xs: 'calc(env(safe-area-inset-bottom) + 16px)', md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, md: 8 },
          mb: { xs: showContactUsBtn ? 18 : 9, md: 0 },
          maxWidth: '100vw',
          overflowX: 'clip'
        }}
      >
        <Outlet context={{ smbNavState }} />
      </Box>

      {/* Mobile Bottom Navigation (Scrollable Ribbon) */}
      <Paper 
        sx={{ 
          display: { xs: 'flex', md: 'none' },
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          zIndex: theme.zIndex.appBar,
          borderTop: '1px solid rgba(0,0,0,0.1)',
          // Safe area support for modern phones
          pb: 'env(safe-area-inset-bottom)',
          flexDirection: 'column',
          borderRadius: '12px 12px 0 0',
        }} 
        elevation={3}
      >
        {showContactUsBtn && (
          <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'center' }}>
            <Button
              component="a"
              href={contactUsWhatsAppURL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="small"
              fullWidth
              startIcon={<WhatsAppIcon />}
              sx={{
                borderRadius: 2,
                py: 1,
                fontWeight: 'bold',
                textTransform: 'none',
                bgcolor: WHATSAPP_BUTTON_BG_COLOR,
                color: '#fff',
                transition: 'all 0.2s ease-in-out',
                boxShadow: '0 2px 4px rgba(37, 211, 102, 0.2)',
                '&:hover': {
                  bgcolor: WHATSAPP_BUTTON_HOVER_BG_COLOR,
                  boxShadow: '0 4px 8px rgba(37, 211, 102, 0.4)',
                  transform: 'translateY(-1px)',
                }
              }}
            >
              Talk to an advisor
            </Button>
          </Box>
        )}
        <Tabs
          value={location.pathname}
          onChange={(_, newValue) => {
            navigate(newValue)
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTabs-indicator': {
              top: 0,
              bottom: 'auto',
              height: 3,
              borderRadius: '0 0 4px 4px'
            },
            '& .MuiTab-root': {
              minWidth: 80,
              fontSize: '0.7rem',
              py: 1,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
              },
            }
          }}
        >
          {mobileBottomItems.map((item) => (
            <Tab
              key={item.path}
              label={item.text}
              value={item.path}
              icon={
                <Badge>
                  {item.icon}
                </Badge>
              }
            />
          ))}
        </Tabs>
      </Paper>
    </Box>
  )
}

export default Layout
